import React, { useCallback, useEffect, useRef, useState } from 'react'

import Highlighter from 'components/uiKit/Highlighter'
import type { IKitControlProps } from 'components/uiKit/KitTypes'
import { isMixed } from 'components/uiKit/KitUtils'
import type { ITypographyProps } from 'components/uiKit/Typography'
import { Typography } from 'components/uiKit/Typography'

import { restoreCaretPosition, saveCaretPosition, clearBreaks } from './caret'

export interface IEditableProps
  extends Omit<ITypographyProps, 'children' | 'name' | 'onBlur' | 'onFocus' | 'size'>,
    IKitControlProps<string | null> {
  testData?: string
  max?: number
  highlight?: string
}

const Editable = React.forwardRef<HTMLSpanElement, IEditableProps>(
  (
    {
      name,
      defaultValue,
      value = defaultValue,
      onChange,
      onKeyDown,
      onBlur,
      onFocus,
      disabled,
      readOnly,
      max,
      highlight,
      tabIndex = 0,
      rows,
      error,
      autoFocus,
      cursor,
      ...rest
    },
    outerRef,
  ) => {
    const safeValue = isMixed(value) ? '' : ((value as string | null | undefined) ?? '')

    const [focused, setFocused] = useState(false)
    const localRef = useRef<HTMLSpanElement>(null)
    const ref = (outerRef || localRef) as React.MutableRefObject<HTMLSpanElement | null>
    const [caret, setCaret] = useState(safeValue.length)

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLSpanElement>) => {
        if (max && e.target.innerText.length > max) {
          e.target.innerText = safeValue
          onChange?.(clearBreaks((safeValue || e.target.innerText).slice(0, max + 1)))
          restoreCaretPosition(ref, safeValue.length)
        } else {
          onChange?.(clearBreaks(e.target.innerText))
        }

        const caret = saveCaretPosition(ref) || safeValue.length
        setCaret(caret)
      },
      [ref, max, safeValue, onChange],
    )

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLSpanElement>) => {
        onKeyDown?.(e)
        if (e.key === 'Enter') {
          e.preventDefault()
        }
      },
      [onKeyDown],
    )
    const handleFocus = useCallback(
      (e: React.FocusEvent) => {
        onFocus?.(e)
        setFocused(true)
      },
      [onFocus],
    )

    const handleBlur = useCallback(
      (e: React.FocusEvent) => {
        onBlur?.(e)
        setFocused(false)
      },
      [onBlur],
    )

    useEffect(() => {
      if (focused) {
        restoreCaretPosition(ref, caret)
      }
    }, [caret, focused, ref, safeValue])

    useEffect(() => {
      if (autoFocus) {
        ref.current?.focus()
      }
    }, [autoFocus, ref])

    return (
      <Typography
        {...rest}
        contentEditable={!disabled && !readOnly}
        cursor={(cursor ?? (!disabled && !readOnly)) ? 'text' : 'default'}
        focused={focused}
        name={name}
        onBlur={handleBlur}
        onFocus={handleFocus}
        onInput={handleChange}
        onKeyDown={handleKeyDown}
        ref={ref}
        rows={focused && !disabled && !readOnly ? 0 : rows}
        tabIndex={!disabled && !readOnly ? tabIndex : -1}
        suppressContentEditableWarning
      >
        {safeValue && highlight ? (
          <Highlighter search={highlight} text={safeValue} wordBreak='break-word' />
        ) : (
          safeValue
        )}
      </Typography>
    )
  },
)

Editable.displayName = 'Editable'

export default React.memo(Editable)
